// src/pages/BankMaster.tsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    Button,
    Badge,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Label,
    Input,
    Alert,
    Spinner,
} from 'reactstrap';
import {
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
} from 'reactstrap';
import { Plus, Edit, Trash2, RefreshCw, Eye, CheckCircle, XCircle, Archive, Search, MoreVertical, ChevronDown } from 'react-feather';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import TableContainer from "../../Components/Common/TableContainer";
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { apiClient } from '../../helpers/api_helper';

// Types
interface Bank {
    id: number;
    bankname: string;
    description: string | null;
    status: boolean;
    created_at: string;
    updated_at: string;
    employeeCount?: number;
    employees?: any[];
}
interface BankActionDropdownProps {
    bank: any;
    onView: (bank: any) => void;
    onEdit: (bank: any) => void;
}

const BankActionDropdown: React.FC<BankActionDropdownProps> = ({
    bank,
    onView,
    onEdit,
}) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const toggle = () => setDropdownOpen(!dropdownOpen);

    return (
        <div className="d-flex gap-1 justify-content-center align-items-center">
            <Dropdown isOpen={dropdownOpen} toggle={toggle} direction="down">
                <DropdownToggle
                    tag="button"
                    className="btn btn-secondary btn-sm d-flex align-items-center gap-1"
                    size="sm"
                >
                    Action <ChevronDown size={14} />
                </DropdownToggle>
                <DropdownMenu>
                    <DropdownItem
                        onClick={() => {
                            toggle();
                            onView(bank);
                        }}
                        className="text-info"
                    >
                        <Eye size={14} className="me-2" />
                        View
                    </DropdownItem>
                    <DropdownItem
                        onClick={() => {
                            toggle();
                            onEdit(bank);
                        }}
                        className="text-primary"
                    >
                        <Edit size={14} className="me-2" />
                        Edit
                    </DropdownItem>
                </DropdownMenu>
            </Dropdown>
        </div>
    );
};

const BankMaster: React.FC = () => {
    // State
    const [banks, setBanks] = useState<Bank[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showInactive, setShowInactive] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);

    // Modal states
    const [modal, setModal] = useState(false);
    const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
    const [selectedBank, setSelectedBank] = useState<Bank | null>(null);
    const [formData, setFormData] = useState({
        bankname: '',
        description: '',
        status: true,
    });
    const [submitting, setSubmitting] = useState(false);
    const [formError, setFormError] = useState('');

    // Delete modal
    const [deleteModal, setDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [bankToDelete, setBankToDelete] = useState<Bank | null>(null);

    // View modal
    const [viewModal, setViewModal] = useState(false);
    const [viewingBank, setViewingBank] = useState<Bank | null>(null);

    // Fetch banks with filters
    const fetchBanks = useCallback(async () => {
        setLoading(true);
        setError('');

        try {
            const params: any = {
                page: currentPage + 1,
                limit: pageSize,
                sortBy: 'bankname',
                sortOrder: 'asc',
            };

            if (searchTerm) {
                params.search = searchTerm;
            }

            if (!showInactive) {
                params.status = true;
            }

            const response = await apiClient.get('/banks', { params });
            console.log('API Response:', response);

            const responseData = response;
            const data = responseData.data || [];
            const pageInfo = responseData.pagination || {};

            setBanks(data);
            setTotalCount(pageInfo.totalCount || 0);
            setCurrentPage((pageInfo.page || 1) - 1);
            setPageSize(pageInfo.limit || 10);
        } catch (error: any) {
            console.error('Error fetching banks:', error);
            setError(error.message || 'Failed to fetch banks');
            toast.error('Failed to load banks');
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, searchTerm, showInactive]);

    // Initial fetch
    useEffect(() => {
        fetchBanks();
    }, [fetchBanks]);

    // Handle search with debounce
    useEffect(() => {
        const timer = setTimeout(() => {
            if (currentPage === 0) {
                fetchBanks();
            } else {
                setCurrentPage(0);
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Handle page change
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    // Handle page size change
    const handlePageSizeChange = (size: number) => {
        setPageSize(size);
        setCurrentPage(0);
    };

    // Handle form input change
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        const checked = (e.target as HTMLInputElement).checked;

        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    // Open add modal
    const openAddModal = () => {
        setModalMode('add');
        setFormData({
            bankname: '',
            description: '',
            status: true,
        });
        setFormError('');
        setModal(true);
    };

    // Open edit modal
    const openEditModal = (bank: Bank) => {
        setModalMode('edit');
        setSelectedBank(bank);
        setFormData({
            bankname: bank.bankname,
            description: bank.description || '',
            status: bank.status,
        });
        setFormError('');
        setModal(true);
    };

    // Close modal
    const closeModal = () => {
        setModal(false);
        setSelectedBank(null);
        setFormError('');
    };

    // Handle form submit
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.bankname.trim()) {
            setFormError('Bank name is required');
            return;
        }

        setSubmitting(true);
        setFormError('');

        try {
            if (modalMode === 'add') {
                await apiClient.post('/banks', {
                    bankname: formData.bankname.trim(),
                    description: formData.description.trim() || undefined,
                    status: formData.status,
                });
                toast.success('Bank created successfully!');
            } else {
                await apiClient.put(`/banks/${selectedBank?.id}`, {
                    bankname: formData.bankname.trim(),
                    description: formData.description.trim() || undefined,
                    status: formData.status,
                });
                toast.success('Bank updated successfully!');
            }

            closeModal();
            fetchBanks();
        } catch (error: any) {
            console.error('Error saving bank:', error);
            setFormError(error.message || 'Failed to save bank');
            toast.error('Failed to save bank');
        } finally {
            setSubmitting(false);
        }
    };

    // Handle delete
    const openDeleteModal = (bank: Bank) => {
        setBankToDelete(bank);
        setDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!bankToDelete) return;

        setDeleting(true);
        try {
            await apiClient.delete(`/banks/${bankToDelete.id}`);
            toast.success('Bank deleted successfully!');
            setDeleteModal(false);
            fetchBanks();
        } catch (error: any) {
            console.error('Error deleting bank:', error);
            toast.error(error.message || 'Failed to delete bank');
        } finally {
            setDeleting(false);
            setBankToDelete(null);
        }
    };

    // Handle restore
    const handleRestore = async (bank: Bank) => {
        try {
            await apiClient.patch(`/banks/${bank.id}/restore`);
            toast.success('Bank restored successfully!');
            fetchBanks();
        } catch (error: any) {
            console.error('Error restoring bank:', error);
            toast.error(error.message || 'Failed to restore bank');
        }
    };

    // Handle toggle status
    const handleToggleStatus = async (bank: Bank) => {
        try {
            await apiClient.patch(`/banks/${bank.id}/toggle-status`);
            toast.success(`Bank ${bank.status ? 'deactivated' : 'activated'} successfully!`);
            fetchBanks();
        } catch (error: any) {
            console.error('Error toggling bank status:', error);
            toast.error(error.message || 'Failed to toggle bank status');
        }
    };

    // Handle view
    const handleView = (bank: Bank) => {
        setViewingBank(bank);
        setViewModal(true);
    };

    // Format date
    const formatDate = (dateString: string) => {
        try {
            return format(new Date(dateString), 'dd MMM yyyy HH:mm');
        } catch {
            return 'N/A';
        }
    };

    // Get status badge
    const getStatusBadge = (status: boolean) => {
        return status ? (
            <Badge color="success">
                <CheckCircle size={12} className="me-1" />
                Active
            </Badge>
        ) : (
            <Badge color="warning">
                <XCircle size={12} className="me-1" />
                Inactive
            </Badge>
        );
    };

    // Table columns
    const columns = useMemo(
        () => [
            {
                header: 'SI.No',
                accessorKey: 'id',
                enableColumnFilter: false,
                enableSorting: false,
                cell: (cell: any) => {
                    const { row } = cell;
                    return <strong>{currentPage * pageSize + row.index + 1}</strong>;
                },
            },
            {
                header: 'Bank Name',
                accessorKey: 'bankname',
                cell: (cell: any) => (
                    <span className="fw-semibold">{cell.getValue()}</span>
                ),
            },
            {
                header: 'Description',
                accessorKey: 'description',
                cell: (cell: any) => cell.getValue() || '—',
            },
            {
                header: 'Employees',
                accessorKey: 'employeeCount',
                cell: (cell: any) => {
                    const count = cell.getValue() || 0;
                    return (
                        <Badge color="info" pill>
                            {count} {count === 1 ? 'Employee' : 'Employees'}
                        </Badge>
                    );
                },
            },
            {
                header: 'Status',
                accessorKey: 'status',
                cell: (cell: any) => getStatusBadge(cell.getValue()),
            },
            {
                header: 'Actions',
                accessorKey: 'actions',
                enableColumnFilter: false,
                enableSorting: false,
                cell: (cell: any) => {
                    const bank = cell.row.original;
                    return (
                        <BankActionDropdown
                            bank={bank}
                            onView={handleView}
                            onEdit={openEditModal}
                        />
                    );
                },
            },
        ],
        [currentPage, pageSize]
    );

    return (
        <div className="page-content">
            <Container fluid>
                <BreadCrumb title="Bank Master" pageTitle="Banks" />

                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardHeader className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <h5 className="card-title mb-0">Banks</h5>
                                <div className="d-flex gap-2 align-items-center flex-wrap">
                                    <div className="position-relative" style={{ width: '200px' }}>
                                        <Input
                                            type="text"
                                            placeholder="Search banks..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="ps-4"
                                            bsSize="sm"
                                        />
                                        <Search
                                            size={14}
                                            className="position-absolute top-50 translate-middle-y text-muted"
                                            style={{ left: '10px' }}
                                        />
                                    </div>

                                    <FormGroup check className="mb-0 d-flex align-items-center">
                                        <Label check className="me-2 mb-0">
                                            <Input
                                                type="checkbox"
                                                checked={showInactive}
                                                onChange={() => {
                                                    setShowInactive(!showInactive);
                                                    setCurrentPage(0);
                                                }}
                                            />
                                            <span className="ms-1">Show Inactive</span>
                                        </Label>
                                    </FormGroup>

                                    <Button
                                        color="secondary"
                                        size="sm"
                                        onClick={() => fetchBanks()}
                                        disabled={loading}
                                    >
                                        <RefreshCw size={14} className="me-1" />
                                        Refresh
                                    </Button>

                                    <Button
                                        color="primary"
                                        size="sm"
                                        onClick={openAddModal}
                                    >
                                        <Plus size={14} className="me-1" />
                                        Add Bank
                                    </Button>
                                </div>
                            </CardHeader>

                            <CardBody>
                                {error && (
                                    <Alert color="danger" className="mb-3">
                                        <i className="ri-error-warning-line align-middle me-2"></i>
                                        {error}
                                    </Alert>
                                )}

                                {loading ? (
                                    <div className="text-center py-5">
                                        <Spinner color="primary" className="me-2" />
                                        <span>Loading banks...</span>
                                    </div>
                                ) : (
                                    <TableContainer
                                        columns={columns}
                                        data={banks}
                                        customPageSize={pageSize}
                                        tableClass="table-striped"
                                        theadClass="table-light"
                                        manualPagination={true}
                                        pageCount={Math.ceil(totalCount / pageSize)}
                                        currentPage={currentPage}
                                        onPageChange={handlePageChange}
                                        onPageSizeChange={handlePageSizeChange}
                                        totalRecords={totalCount}
                                        showPageSizeOptions={true}
                                        pageSizeOptions={[10, 25, 50, 100]}
                                    />
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* Add/Edit Modal */}
            <Modal isOpen={modal} toggle={closeModal} size="lg">
                <ModalHeader toggle={closeModal}>
                    {modalMode === 'add' ? 'Add New Bank' : 'Edit Bank'}
                </ModalHeader>
                <ModalBody>
                    {formError && (
                        <Alert color="danger" className="mb-3">
                            {formError}
                        </Alert>
                    )}
                    <Form onSubmit={handleSubmit}>
                        <Row className="g-3">
                            <Col md={12}>
                                <FormGroup>
                                    <Label className="form-label-sm">Bank Name *</Label>
                                    <Input
                                        type="text"
                                        name="bankname"
                                        value={formData.bankname}
                                        onChange={handleInputChange}
                                        bsSize="sm"
                                        placeholder="Enter bank name"
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={12}>
                                <FormGroup>
                                    <Label className="form-label-sm">Description</Label>
                                    <Input
                                        type="textarea"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        bsSize="sm"
                                        rows={3}
                                        placeholder="Enter description (optional)"
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={12}>
                                <FormGroup check>
                                    <Label check>
                                        <Input
                                            type="checkbox"
                                            name="status"
                                            checked={formData.status}
                                            onChange={handleInputChange}
                                        />
                                        <span className="ms-2">Active</span>
                                    </Label>
                                </FormGroup>
                            </Col>
                        </Row>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button color="light" onClick={closeModal} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        onClick={handleSubmit}
                        disabled={submitting}
                    >
                        {submitting ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Saving...
                            </>
                        ) : (
                            modalMode === 'add' ? 'Create' : 'Update'
                        )}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
                <ModalHeader toggle={() => setDeleteModal(false)}>
                    Confirm Delete
                </ModalHeader>
                <ModalBody>
                    <p>
                        Are you sure you want to delete the bank{' '}
                        <strong>"{bankToDelete?.bankname}"</strong>?
                    </p>
                    {bankToDelete?.employeeCount && bankToDelete.employeeCount > 0 && (
                        <Alert color="danger" className="mb-0">
                            <i className="ri-alert-line me-2"></i>
                            This bank has {bankToDelete.employeeCount} active employee(s).
                            You cannot delete it until you reassign or deactivate these employees.
                        </Alert>
                    )}
                    {(!bankToDelete?.employeeCount || bankToDelete.employeeCount === 0) && (
                        <Alert color="warning" className="mb-0">
                            <i className="ri-alert-line me-2"></i>
                            This action will deactivate the bank. You can restore it later.
                        </Alert>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="light" onClick={() => setDeleteModal(false)} disabled={deleting}>
                        Cancel
                    </Button>
                    <Button
                        color="danger"
                        onClick={confirmDelete}
                        disabled={deleting || (bankToDelete?.employeeCount || 0) > 0}
                    >
                        {deleting ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Deleting...
                            </>
                        ) : (
                            'Delete'
                        )}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* View Modal */}
            <Modal isOpen={viewModal} toggle={() => setViewModal(false)} size="lg">
                <ModalHeader toggle={() => setViewModal(false)}>
                    Bank Details
                </ModalHeader>
                <ModalBody>
                    {viewingBank && (
                        <div>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <strong>Bank Name:</strong>
                                </Col>
                                <Col md={6}>
                                    {viewingBank.bankname}
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <strong>Description:</strong>
                                </Col>
                                <Col md={6}>
                                    {viewingBank.description || '—'}
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <strong>Status:</strong>
                                </Col>
                                <Col md={6}>
                                    {getStatusBadge(viewingBank.status)}
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <strong>Total Employees:</strong>
                                </Col>
                                <Col md={6}>
                                    <Badge color="info" pill>
                                        {viewingBank.employeeCount || 0} Employees
                                    </Badge>
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <strong>Created At:</strong>
                                </Col>
                                <Col md={6}>
                                    {formatDate(viewingBank.created_at)}
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <strong>Updated At:</strong>
                                </Col>
                                <Col md={6}>
                                    {formatDate(viewingBank.updated_at)}
                                </Col>
                            </Row>

                            {viewingBank.employees && viewingBank.employees.length > 0 && (
                                <>
                                    <hr />
                                    <h6 className="mb-3">Associated Employees</h6>
                                    <div className="table-responsive">
                                        <table className="table table-sm table-bordered">
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>Employee Code</th>
                                                    <th>Name</th>
                                                    <th>Phone</th>
                                                    <th>Designation</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {viewingBank.employees.map((emp, index) => (
                                                    <tr key={emp.id}>
                                                        <td>{index + 1}</td>
                                                        <td>{emp.employee_code || '—'}</td>
                                                        <td>{emp.name}</td>
                                                        <td>{emp.phone || '—'}</td>
                                                        <td>{emp.designation?.title || '—'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setViewModal(false)}>
                        Close
                    </Button>
                    {viewingBank && (
                        <Button
                            color="primary"
                            onClick={() => {
                                setViewModal(false);
                                openEditModal(viewingBank);
                            }}
                        >
                            <Edit size={14} className="me-1" />
                            Edit
                        </Button>
                    )}
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default BankMaster;
